import { useState, useEffect, useRef, useMemo } from "react"
import { FaEdit, FaTrash, FaSms } from "react-icons/fa"
import { Link, useNavigate } from "react-router-dom"
import { toast } from 'react-toastify'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import classnames from 'classnames'
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const Villages = () => {
    const [villages, setVillages] = useState([]);
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [title, setTitle] = useState('Villages List');
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [show, setShow] = useState(false);
    const [publicID, setPublicID] = useState('');
    const gridRef = useRef();
    const navigate = useNavigate();

    useEffect(() => {
        fetchData()
    }, [])

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: (params) => {
                if (isNaN(params.node.id)) return "";
                return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 180,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                const hasZones = e.data.zones > 0;
                return (
                    <div className="d-flex gap-1">
                        <Link
                            className="btn btn-outline-info"
                            to="/send-location-message"
                            state={{ payload: { id: e.data.id, by: 'village' } }}
                            title="Send SMS"
                        >
                            <FaSms />
                        </Link>
                        <Link
                            className="btn btn-outline-info"
                            to="/update-village"
                            state={{ village: e.data }}
                            title="Edit"
                        >
                            <FaEdit />
                        </Link>
                        <button
                            className={classnames('btn btn-outline-danger', { disabled: hasZones })}
                            onClick={() => !hasZones && handleShow(e.data.public_id)}
                            title={hasZones ? "Cannot delete (has zones)" : "Delete"}
                        >
                            <FaTrash />
                        </button>
                    </div>
                );
            },
        },
        {
            headerName: "Name",
            field: "name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Zones",
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return `${e.data.zones}`;
                return <Link to="#" state={{ payload: { public_id: e.data.public_id, name: e.data.name } }}>{e.data.zones}</Link>;
            },
        },
        {
            headerName: "Locations",
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return `${e.data.locations}`;
                return <Link to="/village-locations" state={{ payload: { public_id: e.data.public_id, name: e.data.name } }}>{e.data.locations}</Link>;
            },
        },
        {
            headerName: "Active",
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return `${e.data.active}`;
                return <Link to="/village-locations" state={{ payload: { public_id: e.data.public_id, name: e.data.name, status: 'Active' } }}>{e.data.active}</Link>;
            },
        },
        {
            headerName: "Inactive",
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return `${e.data.inactive}`;
                return <Link to="/village-locations" state={{ payload: { public_id: e.data.public_id, name: e.data.name, status: 'Inactive' } }}>{e.data.inactive}</Link>;
            },
        },
    ], []);

    const fetchData = async () => {
        setIsLoading(true)
        try {
            const response = await Server.get('/villages/')
            setVillages(response?.data);

            const output = response?.data.reduce((data, row) => {
                data.locations += row.locations;
                data.active += row.active;
                data.inactive += row.inactive;
                return data;
            }, {locations: 0, active: 0, inactive: 0});
            setPinnedBottomRowData([
                {
                    locations: output.locations,
                    active: output.active,
                    inactive: output.inactive,
                }
            ]);
        } catch (error) {
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error.response.data.detail);
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load villages')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleClose = () => {
        if (isDeleting) return;
        setShow(false)
    }
    const handleShow = (public_id) => {
        setShow(true)
        setPublicID(public_id)
    };

    const handleDelete = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.delete(`/villages/${public_id}`)
            toast.success(response.data.detail)
            setVillages(prev => prev.filter((village) => village.public_id !== public_id))
            setShow(false)
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error.response.data.detail);
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to delete village')
            }
        } finally {
            setIsDeleting(false)
        }
    }

    const defaultColDef = useMemo(() => ({
        filterParams: { buttons: [] }
    }), []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
            return {
                backgroundColor: '#f8f9fa',
                fontWeight: 'bold',
                color: '#000',
            };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <div className="row">
            <div className="col-md-12">
                <Link to='/add-village' className="btn btn-outline-info mb-2" style={{float: 'right'}}>Add Village</Link>
            </div>
        </div>
        <div className="card">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">{ title }</h3>
            </div>
            <div className="card-body">
                <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                    <AgGridReact
                        suppressMenuHide={true}
                        onFirstDataRendered={onFirstDataRendered}
                        onGridSizeChanged={onGridSizeChanged}
                        defaultColDef={defaultColDef}
                        rowData={villages}
                        domLayout={"normal"}
                        rowSelection={"multiple"}
                        pagination={false}
                        resizable={true}
                        animateRows={true}
                        enableCellTextSelection={true}
                        suppressCellFocus={true}
                        ref={gridRef}
                        pinnedBottomRowData={pinnedBottomRowData}
                        getRowStyle={getRowStyle}
                        columnDefs={columnDefs}
                    />
                </div>
            </div>
        </div>

        <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
            <Modal.Header closeButton={!isDeleting}>
                <Modal.Title>Delete Village</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to delete this village? This action cannot be undone.</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isDeleting}>
                    No
                </Button>
                <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
                    {isDeleting ? 'Deleting…' : 'Yes, delete'}
                </Button>
            </Modal.Footer>
        </Modal>
    </>
  )
}

export default Villages
